import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../app/hooks';
import { getValueFromResource, normalizeDateFormat } from '../../common/timeLiveFunction';
import { DateRangePicker } from 'rsuite';
import DatePickerRanges from '../../common/DatePickerRanges';
import { CurrentCultureDateFormat } from '../../common/UiUtilities';
import moment from 'moment';
import fetchAPI from '../../common/fetchAPI';
import fetchTimeliveAPI from '../../common/fetchTimeliveAPI';
import Select, { StylesConfig } from 'react-select';
import MultiSelectDropdown, { MultiSelectOption } from '../../common/Multiselectdropdown';
import FilterAccordion from '../../common/FilterAccordion';

const STATUS_OPTIONS: MultiSelectOption[] = [
    { value: 'Present', label: 'Present' },
    { value: 'Absent', label: 'Absent' },
    { value: 'Half Day', label: 'Half Day' },
    { value: 'Short Day', label: 'Short Day' },
    { value: 'Other', label: 'Other' },
]

const EMPLOYEE_STATUS_ALL = -1;
const EMPLOYEE_STATUS_ACTIVE = 0;

const singleSelectStyles: StylesConfig<MultiSelectOption, false> = {
    control: (base, state) => ({
        ...base,
        minHeight: 40,
        borderRadius: 4,
        boxShadow: state.isFocused ? '0 0 0 3px rgba(99,102,241,0.15)' : 'none',
        padding: '2px 6px',
        cursor: 'pointer',
    }),
    valueContainer: (base) => ({ ...base, padding: '2px 4px' }),
    singleValue: (base) => ({ ...base, fontSize: 15, color: '#111827' }),
    placeholder: (base) => ({ ...base, fontSize: 15, color: '#9CA3AF' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base, state) => ({
        ...base,
        color: '#9CA3AF',
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 150ms ease',
        cursor: 'default',
    }),
    menu: (base) => ({
        ...base,
        marginTop: 6,
        borderRadius: 4,
        border: '1px solid #F3F4F6',
        boxShadow: '0 10px 30px rgba(0,0,0,0.10)',
        overflow: 'hidden',
        padding: 6,
        zIndex: 1200,
    }),
    menuList: (base) => ({ ...base, padding: 0, maxHeight: 280 }),
    option: (base, state) => ({
        ...base,
        borderBottom: '1px solid #F3F4F6',
        padding: '10px 12px',
        borderRadius: 4,
        fontSize: 13,
        fontWeight: state.isSelected ? 600 : 400,
        color: state.isSelected ? '#4338CA' : '#111827',
        backgroundColor: state.isFocused ? '#F9FAFB' : 'transparent',
        cursor: 'pointer',
    }),
};

const AttendanceFiltersNew = (props: any) => {
    const { setShowFilters, onApplyFilter, onClearFilter } = props;
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [departmentOptions, setDepartmentOptions] = useState<MultiSelectOption[]>([]);
    const [selectedDepartments, setSelectedDepartments] = useState<MultiSelectOption[]>([]);
    const [locationOptions, setLocationOptions] = useState<MultiSelectOption[]>([]);
    const [selectedLocations, setSelectedLocations] = useState<MultiSelectOption[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<MultiSelectOption[]>(STATUS_OPTIONS);
    const [employeeTypeOptions, setEmployeeTypeOptions] = useState<MultiSelectOption[]>([]);
    const [selectedEmployeeTypes, setSelectedEmployeeTypes] = useState<MultiSelectOption[]>([]);
    const [attendanceSourceOptions, setAttendanceSourceOptions] = useState<MultiSelectOption[]>([]);
    const [selectedSources, setSelectedSources] = useState<MultiSelectOption[]>([]);
    const [employeeList, setEmployeeList] = useState<MultiSelectOption[]>([]);
    const [selectedEmployees, setSelectedEmployees] = useState<MultiSelectOption[]>([]);
    const [selectedEmployeeStatus, setSelectedEmployeeStatus] = useState<number>(EMPLOYEE_STATUS_ACTIVE);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isDateChanged, setIsDateChanged] = useState(false);
    const [isEmployeeChanged, setIsEmployeeChanged] = useState(false);
    const defaultDateRef = useRef(new Date());
    const AccountEmployeeId = localStorage.getItem('accountEmployeeId');

    const toIdParam = (selected: MultiSelectOption[]) =>
        selected.map((s) => s.value).join(',');

    const buildPayload = (sd: Date, ed: Date, depts: MultiSelectOption[], locs: MultiSelectOption[], statuses: MultiSelectOption[], empTypes: MultiSelectOption[], sources: MultiSelectOption[], emps: MultiSelectOption[], employeeStatus: number) => ({
        StartDate: moment(sd).format('YYYY-MM-DD'),
        EndDate: moment(ed).format('YYYY-MM-DD'),
        AccountEmployeeId,
        searchEmployeeIds: emps.length > 0 ? toIdParam(emps) : toIdParam(employeeList),
        accountDepartmentIds: toIdParam(depts),
        accountLocationIds: toIdParam(locs),
        statusIds: toIdParam(statuses),
        employeeTypeIds: toIdParam(empTypes),
        attendanceSourceIds: toIdParam(sources),
        employeeStatus: employeeStatus,
    })

    const getAllDepartments = async () => {
        try {
            const response = await fetchTimeliveAPI('api/Departments/GetAccountEmployeeAttendanceDepartments', 'Get', '', true, '32f92e58-03dc-4d13-960f-e34e26d3e6a5');
            const json = await response.json();
            const options: MultiSelectOption[] = json?.Data?.map((item: any) => ({
                label: item?.DepartmentName,
                value: item?.AccountDepartmentId
            })) ?? [];
            setDepartmentOptions(options);
            setSelectedDepartments(options);
        } catch (error) {
            console.log(error);
        }
    }

    const getAllLocations = async () => {
        try {
            const response = await fetchTimeliveAPI('api/Locations/GetAccountEmployeeAttendanceLocations', 'GET', '', true, '0df3ab0c-4033-4013-8b1e-2f0b64f8cbab');
            const json = await response.json();
            const options: MultiSelectOption[] = json?.Data?.map((item: any) => ({
                label: item?.AccountLocation,
                value: item?.AccountLocationId
            })) ?? [];
            setLocationOptions(options);
            setSelectedLocations(options);
        } catch (error) {
            console.log(error);
        }
    }

    const getAllEmployeeTypes = async () => {
        try {
            const response = await fetchTimeliveAPI('api/v2/EmployeeTypes', 'GET', '', true, '0df3ab0c-4033-4013-8b1e-2f0b64f8cbab');
            const json = await response.json();
            const options: MultiSelectOption[] = json?.Data?.map((item: any) => ({
                label: item?.AccountEmployeeType,
                value: item?.AccountEmployeeTypeId
            })) ?? [];
            setEmployeeTypeOptions(options);
            setSelectedEmployeeTypes(options);
        } catch (error) {
            console.log(error);
        }
    }

    const getAllAttendanceSources = async () => {
        try {
            const response = await fetchTimeliveAPI('api/SystemAttendanceSource', 'GET', '', true, '0df3ab0c-4033-4013-8b1e-2f0b64f8cbab');
            const json = await response.json();
            const options: MultiSelectOption[] = json?.Data?.map((item: any) => ({
                label: item?.SystemEntryMode,
                value: item?.SystemEntryModeId
            })) ?? [];
            setAttendanceSourceOptions(options);
            setSelectedSources(options);
        } catch (error) {

        }
    }

    const getAllEmployees = async () => {
        try {
            const response = await fetchTimeliveAPI("api/Employee/GetAccountEmployees", "Get", "", true);
            const json = await response.json();
            const options: MultiSelectOption[] = json?.Data?.map((item: any) => ({
                label: item?.AccountEmployeeName,
                value: item?.AccountEmployeeId
            })) ?? [];
            setEmployeeList(options);
            const matchedEmployee = options.find((opt) => String(opt.value) === String(AccountEmployeeId));
            setSelectedEmployees(matchedEmployee ? [matchedEmployee] : []);
            // setSelectedEmployees(options);
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        getAllDepartments();
        getAllLocations();
        getAllEmployeeTypes();
        getAllAttendanceSources();
        getAllEmployees();
    }, [])

    const handleClearFilter = () => {
        const resetStart = new Date();
        const resetEnd = new Date();

        const matchedEmployee = employeeList.find((opt) => String(opt.value) === String(AccountEmployeeId));
        const defaultEmployees = matchedEmployee ? [matchedEmployee] : [];

        setStartDate(resetStart);
        setEndDate(resetEnd);
        setSelectedDepartments(departmentOptions);
        setSelectedLocations(locationOptions);
        setSelectedStatuses(STATUS_OPTIONS);
        setSelectedEmployeeTypes(employeeTypeOptions);
        setSelectedSources(attendanceSourceOptions);
        setSelectedEmployees(defaultEmployees);
        setSelectedEmployeeStatus(EMPLOYEE_STATUS_ACTIVE);
        setIsDateChanged(false);
        setIsEmployeeChanged(false);

        const payload = buildPayload(resetStart, resetEnd, departmentOptions, locationOptions, STATUS_OPTIONS, employeeTypeOptions, attendanceSourceOptions, defaultEmployees, EMPLOYEE_STATUS_ACTIVE);
        onClearFilter?.(payload);
        onApplyFilter?.(payload);
    };

    const handleApplyFilter = () => {
        setShowFilters(false);
        setIsFilterOpen(false);
        const payload = buildPayload(startDate, endDate, selectedDepartments, selectedLocations, selectedStatuses, selectedEmployeeTypes, selectedSources, selectedEmployees, selectedEmployeeStatus);
        onClearFilter?.(payload);
        onApplyFilter?.(payload)
    }
    const activeFilterCount = useMemo(() => {
        let count = 0;

        if (isEmployeeChanged) count++;
        if (isDateChanged) count++;
        if (selectedDepartments.length > 0 && selectedDepartments.length < departmentOptions.length) count++;
        if (selectedLocations.length > 0 && selectedLocations.length < locationOptions.length) count++;
        if (selectedStatuses.length > 0 && selectedStatuses.length < STATUS_OPTIONS.length) count++;
        if (selectedEmployeeTypes.length > 0 && selectedEmployeeTypes.length < employeeTypeOptions.length) count++;
        if (selectedSources.length > 0 && selectedSources.length < attendanceSourceOptions.length) count++;
        if (selectedEmployeeStatus !== EMPLOYEE_STATUS_ACTIVE) count++;
        // count++;

        return count;
    }, [selectedEmployees, isEmployeeChanged, isDateChanged, selectedDepartments, departmentOptions.length, selectedLocations, locationOptions.length, selectedStatuses, selectedEmployeeTypes, employeeTypeOptions.length, selectedSources, attendanceSourceOptions.length, selectedEmployeeStatus]);

    const selectAllLabel = getValueFromResource('Select All', dispatch, t);
    const allSelectedLabel = getValueFromResource('All selected', dispatch, t);
    const employeeStatusOptions: MultiSelectOption[] = [
        { value: EMPLOYEE_STATUS_ALL, label: getValueFromResource('All', dispatch, t) },
        { value: EMPLOYEE_STATUS_ACTIVE, label: getValueFromResource('Active', dispatch, t) },
        { value: 1, label: getValueFromResource('Inactive', dispatch, t) },
    ];
    const selectedEmployeeStatusOption = employeeStatusOptions.find((option) => option.value === selectedEmployeeStatus) ?? employeeStatusOptions[0];

    return (
        <FilterAccordion
            title={getValueFromResource('Filters', dispatch, t)}
            open={isFilterOpen}
            onToggle={setIsFilterOpen}
            activeCount={activeFilterCount}
            onClear={handleClearFilter}
            footer={
                <>
                    {/* <button
                        className="btn btn-secondary btn-bs-btn-font-weight btn-sm"
                        onClick={handleClearFilter}
                    >
                        {getValueFromResource("Clear Filter", dispatch, t)}
                    </button> */}
                    <button
                        className="btn btn-primary btn-bs-btn-font-weight btn-sm"
                        onClick={handleApplyFilter}
                    >
                        {getValueFromResource("Apply Filters", dispatch, t)}
                    </button>
                </>
            }
        >
            <div className='row g-3'>
                <div className='col-12 col-sm-6 col-md-3' style={{ zIndex: 0 }}>
                    <label className="fs-7 fw-normal mb-2">
                        {getValueFromResource('Date Range', dispatch, t)}
                    </label>
                    <DateRangePicker
                        ranges={DatePickerRanges()}
                        value={[startDate, endDate]}
                        onChange={(value) => {
                            if (value) {
                                setStartDate(value[0]);
                                setEndDate(value[1]);
                                setIsDateChanged(true);
                            }
                        }}
                        placeholder='Please Select Date Range'
                        cleanable={false}
                        character={' - '}
                        placement="bottomEnd"
                        format={normalizeDateFormat(CurrentCultureDateFormat.toString())}
                        style={{ width: '100%' }}
                    />
                </div>
                <div className='col-12 col-sm-6 col-md-3'>
                    <label className="fs-7 fw-normal mb-2">
                        {getValueFromResource('Employee', dispatch, t)}
                    </label>
                    <MultiSelectDropdown
                        options={employeeList}
                        value={selectedEmployees}
                        onChange={(value) => {
                            setSelectedEmployees(value);
                            setIsEmployeeChanged(true);
                        }}
                        placeholder="Select"
                        name="employeelist"
                        enableSelectAll
                        selectAllLabel={selectAllLabel}
                        allSelectedLabel={allSelectedLabel}
                    />
                </div>
                <div className='col-12 col-sm-6 col-md-3'>
                    <label className="fs-7 fw-normal mb-2">
                        {getValueFromResource('Department', dispatch, t)}
                    </label>
                    <MultiSelectDropdown
                        options={departmentOptions}
                        value={selectedDepartments}
                        onChange={setSelectedDepartments}
                        placeholder="Select"
                        name="departmentlist"
                        enableSelectAll
                        selectAllLabel={selectAllLabel}
                        allSelectedLabel={allSelectedLabel}
                    />
                </div>
                <div className='col-12 col-sm-6 col-md-3'>
                    <label className="fs-7 fw-normal mb-2">
                        {getValueFromResource('Location', dispatch, t)}
                    </label>
                    <MultiSelectDropdown
                        options={locationOptions}
                        value={selectedLocations}
                        onChange={setSelectedLocations}
                        placeholder="Select"
                        name="locationlist"
                        enableSelectAll
                        selectAllLabel={selectAllLabel}
                        allSelectedLabel={allSelectedLabel}
                    />
                </div>
            </div>

            <div className='row mt-2 g-3'>
                <div className='col-12 col-sm-6 col-md-3'>
                    <label className="fs-7 fw-normal mb-2">
                        {getValueFromResource('Status', dispatch, t)}
                    </label>
                    <MultiSelectDropdown
                        options={STATUS_OPTIONS}
                        value={selectedStatuses}
                        onChange={setSelectedStatuses}
                        placeholder="Select"
                        name="statuslist"
                        enableSelectAll
                        selectAllLabel={selectAllLabel}
                        allSelectedLabel={allSelectedLabel}
                    />
                </div>
                <div className='col-12 col-sm-6 col-md-3'>
                    <label className="fs-7 fw-normal mb-2">
                        {getValueFromResource('Employee Type', dispatch, t)}
                    </label>
                    <MultiSelectDropdown
                        options={employeeTypeOptions}
                        value={selectedEmployeeTypes}
                        onChange={setSelectedEmployeeTypes}
                        placeholder="Select"
                        name="exceptionlist"
                        enableSelectAll
                        selectAllLabel={selectAllLabel}
                        allSelectedLabel={allSelectedLabel}
                    />
                </div>
                <div className='col-12 col-sm-6 col-md-3'>
                    <label className="fs-7 fw-normal mb-2">
                        {getValueFromResource('Attendance Source', dispatch, t)}
                    </label>
                    <MultiSelectDropdown
                        options={attendanceSourceOptions}
                        value={selectedSources}
                        onChange={setSelectedSources}
                        placeholder="Select"
                        name="sourcelist"
                        enableSelectAll
                        selectAllLabel={selectAllLabel}
                        allSelectedLabel={allSelectedLabel}
                    />
                </div>
                <div className='col-12 col-sm-6 col-md-3'>
                    <label className="fs-7 fw-normal mb-2">
                        {getValueFromResource('Employee Status', dispatch, t)}
                    </label>
                    <Select<MultiSelectOption, false>
                        options={employeeStatusOptions}
                        value={selectedEmployeeStatusOption}
                        onChange={(option) => {
                            if (option) setSelectedEmployeeStatus(Number(option.value));
                        }}
                        isSearchable={false}
                        isClearable={false}
                        placeholder="Select"
                        name="employeeStatus"
                        styles={singleSelectStyles}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                        menuPosition="fixed"
                        classNamePrefix="single-select"
                    />
                </div>
            </div>
        </FilterAccordion>
    );
};

export default AttendanceFiltersNew;